import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { collection, getDocs, doc, updateDoc, onSnapshot, getDoc } from 'firebase/firestore';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { useCompanyId } from '../hooks/useCompanyId';
import { getUserTechStackData, updateUserTechStack } from '../services/userTechnologyService';
import { getAccountById } from '../services/firestoreService';
import BackButton from '../components/BackButton';
import StartSidebar from '../components/StartSidebar';
import { Lock } from 'lucide-react';
import { resolveCoreToolLinks, selectionDisplayName } from '../utils/techStackCoreLinks';
import { DEFAULT_TIER_ORDER, applyTierMeta, compareTechSoftware, compareTechTypes, fetchTechTierMeta } from '../utils/techStackTiers';

const StartTechStack = () => {
  const navigate = useNavigate();
  const { darkMode } = useTheme();
  const { currentUser } = useAuth();
  const { companyId, loading: companyIdLoading } = useCompanyId();
  const [activeLayer, setActiveLayer] = useState(null);
  const [activeTool, setActiveTool] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [tierOrder, setTierOrder] = useState(DEFAULT_TIER_ORDER);
  const [showPercentages, setShowPercentages] = useState(false);
  const [showDetailedCosts, setShowDetailedCosts] = useState(false);
  const [activeTab, setActiveTab] = useState('select');
  const [selectedForComparison, setSelectedForComparison] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [userCount, setUserCount] = useState(1);
  const [userTechStack, setUserTechStack] = useState([]);
  const [showDuplicateAnalysis, setShowDuplicateAnalysis] = useState(false);
  const [duplicateAnalysis, setDuplicateAnalysis] = useState([]);
  const [imageErrors, setImageErrors] = useState(new Set());
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [unlockedToolIds, setUnlockedToolIds] = useState(null); // null = full access, array = filtered by LeadOverview
  const [unlockCheckComplete, setUnlockCheckComplete] = useState(false);
  
  // Define the tech stack structure with empty initial state
  const [techStack, setTechStack] = useState({
    core: {
      id: 'core',
      title: 'Core',
      color: 'bg-red-700',
      width: '40%',
      tools: []
    },
    primary: {
      id: 'primary',
      title: 'Primary',
      color: 'bg-red-600',
      width: '70%',
      tools: []
    },
    secondary: {
      id: 'secondary',
      title: 'Secondary',
      color: 'bg-red-500',
      width: '100%',
      tools: []
    }
  });

  // Check if tech stack is unlocked
  useEffect(() => {
    if (currentUser) {
      checkUnlockStatus();
    }
  }, [currentUser]);

  const checkUnlockStatus = async () => {
    try {
      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
      let accountId = null;
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        accountId = userData.accountId;
        
        if (accountId) {
          try {
            const accountData = await getAccountById(accountId);
            const unlockedTechStack = accountData?.unlockedTechStack;
            
            // Tech stack unlock logic (matches LeadOverview):
            // - true or undefined: full access to all tools
            // - array: full access (legacy - LeadOverview no longer uses per-tool selection)
            // - false: locked
            if (unlockedTechStack === true || unlockedTechStack === undefined || Array.isArray(unlockedTechStack)) {
              setIsUnlocked(true);
              setUnlockedToolIds(null); // null = no filter, show all
            } else {
              setIsUnlocked(false);
              setUnlockedToolIds(null);
            }
          } catch (error) {
            console.error('Error fetching account data:', error);
            setIsUnlocked(false);
            setUnlockedToolIds(null);
          }
        } else {
          setIsUnlocked(false);
          setUnlockedToolIds(null);
        }
      } else {
        setIsUnlocked(false);
        setUnlockedToolIds(null);
      }
      setUnlockCheckComplete(true);
    } catch (error) {
      console.error('Error checking unlock status:', error);
      setIsUnlocked(false);
      setUnlockedToolIds(null);
      setUnlockCheckComplete(true);
    }
  };

  // Fetch data from Firebase with real-time updates
  // isStale lets an unmounted or superseded load (e.g. companyId resolved
  // mid-flight) drop its result instead of overwriting fresher state
  const fetchTechStackData = async (userData = null, toolIdsFilter = null, isStale = () => false) => {
    try {
      const collectionNames = ['Core', 'Primary', 'Secondary'];
      const tierMeta = await fetchTechTierMeta();
      let newTechStack = { ...techStack };
      // Core tool types by id, so Primary/Secondary types can offer "your <Core tool>"
      const coreTypesById = new Map();
      
      // Use provided user data or current userTechStack state
      const currentUserData = userData || userTechStack;
      // toolIdsFilter: null = show all, array = only show tools whose id is in array (from LeadOverview unlockedTechStack)
      const filterToolIds = toolIdsFilter !== null ? new Set(toolIdsFilter) : null;
      console.log('fetchTechStackData - currentUserData:', currentUserData, 'filterToolIds:', filterToolIds);

      for (const collectionName of collectionNames) {
        const typesSnapshot = await getDocs(collection(db, 'technologyStack', collectionName, 'types'));
        const tools = [];

        for (const typeDoc of typesSnapshot.docs) {
          const typeData = typeDoc.data();
          
          const softwareSnapshot = await getDocs(collection(db, 'technologyStack', collectionName, 'types', typeDoc.id, 'softwares'));
          const software = [];

          softwareSnapshot.forEach(softwareDoc => {
            const data = softwareDoc.data();
          
            if (data && data.name) {
              // Convert old useCases format to new useCaseIds format
              const useCaseIds = data.useCaseIds || (data.useCases ? data.useCases.map((_, index) => index.toString()) : []);
              
              software.push({
                id: softwareDoc.id,
                name: data.name,
                description: data.description || '',
                bbaOpinion: data.bbaOpinion || '',
                useCaseIds: useCaseIds,
                pricing: data.pricing || [],
                image: data.image || '',
                suggested: data.suggested || false,
                open: data.open || false,
                order: data.order
              });
            }
          });

          if (collectionName === 'Core') {
            coreTypesById.set(typeDoc.id, { name: typeData.name, software });
          } else {
            software.push(...resolveCoreToolLinks(typeData, coreTypesById, currentUserData, software));
          }

          // Template order if set, else suggested first
          software.sort(compareTechSoftware);

          if (typeData && typeData.name) {
            // Filter by unlocked content from LeadOverview (when unlockedToolIds is set)
            if (filterToolIds !== null && !filterToolIds.has(typeDoc.id)) {
              continue; // Skip this tool - not in unlocked list
            }
            
            // Check if user has a saved selection for this tool
            const savedSelection = currentUserData.find(item => 
              item.categoryId === collectionName.toLowerCase() && item.toolId === typeDoc.id
            );
            
            console.log(`Tool ${typeData.name} (${typeDoc.id}) - savedSelection:`, savedSelection);
            
            tools.push({
              id: typeDoc.id,
              name: typeData.name,
              order: typeData.order,
              software: selectionDisplayName(savedSelection, software), // Saved selection (a linked Core pick follows the Core tool) or empty
              monthlyCost: savedSelection ? (savedSelection.monthlyCost || 0) : 0, // Use saved cost or 0
              annualCost: savedSelection ? (savedSelection.annualCost || 0) : 0, // Use saved cost or 0
              description: typeData.description || '',
              bbaOpinion: '',
              useCaseIds: [],
              useCases: typeData.useCases || [], // Add use cases from type
              availableSoftware: software,
              selectedPlan: savedSelection ? savedSelection.selectedPlan : null, // Preserve selected plan
              userCount: savedSelection ? savedSelection.userCount : 1 // Preserve user count
            });
          }
        }

        // Convert collection name to lowercase for state key
        const stateKey = collectionName.toLowerCase();
        newTechStack[stateKey].tools = tools.sort(compareTechTypes);
      }
      newTechStack = applyTierMeta(newTechStack, tierMeta);

      console.log('Final tech stack with user selections:', newTechStack);
      if (isStale()) return;
      setTierOrder(tierMeta.map((t) => t.id));
      setTechStack(newTechStack);
      setIsLoaded(true);
    } catch (error) {
      console.error('Error fetching tech stack data:', error);
      if (isStale()) return;
      setIsLoaded(true); // Still set loaded to true to show UI even if there's an error
    }
  };

  // Set up real-time listener and fetch data
  useEffect(() => {
    // Wait for the company lookup to settle: loading with companyId=null would
    // read (and later save) the per-user fallback doc for a company user
    if (!currentUser || !isUnlocked || !unlockCheckComplete || companyIdLoading) return;

    let cancelled = false;
    const isStale = () => cancelled;

    // Load selections, then the stack templates with the unlocked filter
    const loadData = async () => {
      const userTechData = await getUserTechStackData(currentUser.uid, companyId || null);
      const userSelections = userTechData.selectedSoftware || [];
      console.log('Loaded user selections:', userSelections);
      if (cancelled) return;
      setUserTechStack(userSelections);

      await fetchTechStackData(userSelections, unlockedToolIds, isStale);
    };

    // Re-run the full load whenever the stack templates change (the initial
    // snapshot fires immediately, so this also covers first load)
    const unsubscribe = onSnapshot(
      collection(db, 'technologyStack'),
      () => {
        loadData();
      },
      (error) => {
        console.error('Error listening to tech stack changes:', error);
      }
    );

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [currentUser, isUnlocked, unlockCheckComplete, unlockedToolIds, companyId, companyIdLoading]);

  // Calculate tiered pricing cost based on user count
  const calculateTieredPricingCost = (plan, userCount) => {
    if (!plan.tieredPricing || !plan.tiers || plan.tiers.length === 0) {
      return Number(plan.monthlyCost) || 0;
    }
    
    // Find the appropriate tier based on user count
    let selectedTier = plan.tiers[0]; // Default to first tier
    
    for (const tier of plan.tiers) {
      const minUsers = Number(tier.minUsers) || 0;
      const maxUsers = Number(tier.maxUsers) || Infinity;
      if (userCount >= minUsers && userCount <= maxUsers) {
        selectedTier = tier;
        break;
      }
    }
    
    // Calculate cost as Base Monthly Cost + (users * price per user)
    const baseCost = Number(selectedTier.baseMonthlyCost) || 0;
    const pricePerUser = Number(selectedTier.pricePerUser) || 0;
    
    return baseCost + (userCount * pricePerUser);
  };

  // Calculate total costs and percentages
  const calculateCosts = () => {
    let totalMonthlyAll = 0;
    let componentCosts = {};
    let softwareCount = 0;
    let integratedCount = 0;
    
    // Calculate totals for each component and overall
    Object.entries(techStack).forEach(([categoryId, category]) => {
      let componentMonthly = 0;
      
      category.tools.forEach(tool => {
        const monthlyCost = parseFloat(tool.monthlyCost) || 0;
        componentMonthly += monthlyCost;
        totalMonthlyAll += monthlyCost;
        
        // Count software and integration
        if (tool.software && tool.software !== 'None' && tool.monthlyCost > 0) {
          softwareCount++;
          // Check if the selected software has open API
          const selectedSoftware = tool.availableSoftware?.find(sw => sw.name === tool.software);
          if (selectedSoftware?.open) {
            integratedCount++;
          }
        }
      });
      
      componentCosts[categoryId] = {
        monthly: componentMonthly,
        annual: componentMonthly * 12
      };
    });
    
    // Calculate percentages of total cost
    Object.keys(componentCosts).forEach(categoryId => {
      componentCosts[categoryId].percentOfTotal = totalMonthlyAll > 0 
        ? (componentCosts[categoryId].monthly / totalMonthlyAll) * 100 
        : 0;
    });
    
    // Calculate tool percentages within each component
    Object.entries(techStack).forEach(([categoryId, category]) => {
      const componentMonthly = componentCosts[categoryId].monthly;
      
      if (componentMonthly > 0) {
        category.tools.forEach(tool => {
          const monthlyCost = parseFloat(tool.monthlyCost) || 0;
          tool.percentOfComponent = (monthlyCost / componentMonthly) * 100;
        });
      } else {
        category.tools.forEach(tool => {
          tool.percentOfComponent = 0;
        });
      }
    });
    
    // Calculate integration percentage
    const integrationPercentage = softwareCount > 0 ? Math.round((integratedCount / softwareCount) * 100) : 0;
    
    return {
      totalMonthly: totalMonthlyAll,
      totalAnnual: totalMonthlyAll * 12,
      integrationPercentage,
      softwareCount,
      integratedCount,
      components: componentCosts
    };
  };

  const costs = calculateCosts();

  // Sync calculated costs and software data to user's tech stack
  useEffect(() => {
    const syncTechStackToUser = async () => {
      if (!currentUser || !isLoaded || !isUnlocked) return;

      try {
        // Get all selected software from the tech stack
        const selectedSoftware = [];
        let totalAnnualCost = 0;
        let softwareCount = 0;
        let integratedCount = 0;

        Object.entries(techStack).forEach(([categoryId, category]) => {
          category.tools.forEach(tool => {
            if (tool.software && tool.software !== 'None') {
              // Find the selected software from available options
              const selectedSoftwareItem = tool.availableSoftware.find(sw => sw.name === tool.software);
              
              if (selectedSoftwareItem) {
                const softwareData = {
                  ...selectedSoftwareItem,
                  categoryId,
                  toolId: tool.id,
                  monthlyCost: tool.monthlyCost,
                  annualCost: tool.annualCost,
                  selectedPlan: tool.selectedPlan || null, // Preserve selected plan
                  userCount: tool.userCount || null, // Preserve user count
                  selectedAt: new Date().toISOString()
                };
                
                selectedSoftware.push(softwareData);
                totalAnnualCost += tool.annualCost;
                softwareCount++;
                
                if (selectedSoftwareItem.open) {
                  integratedCount++;
                }
              }
            } else if (tool.software === 'None') {
              // Include "None" selections in user's tech stack
              const noneSoftwareData = {
                id: 'none',
                name: 'None',
                categoryId,
                toolId: tool.id,
                monthlyCost: 0,
                annualCost: 0,
                selectedAt: new Date().toISOString()
              };
              
              selectedSoftware.push(noneSoftwareData);
            }
          });
        });

        // Update user's tech stack data
        await updateUserTechStack(currentUser.uid, selectedSoftware, companyId || null);
        console.log('Synced tech stack to user:', selectedSoftware);
        
        // Also update the user's tech stack summary data
        const userDocRef = doc(db, 'users', currentUser.uid);
        await userDocRef.update({
          'techStack.summary': {
            annualCost: totalAnnualCost,
            softwareCount,
            integrationPercentage: softwareCount > 0 ? Math.round((integratedCount / softwareCount) * 100) : 0,
            lastUpdated: new Date().toISOString()
          }
        });

      } catch (error) {
        console.error('Error syncing tech stack to user data:', error);
      }
    };

    // Temporarily disable sync to debug user selection restoration
    // syncTechStackToUser();
  }, [techStack, currentUser, isLoaded, isUnlocked]);

  const handleLayerClick = (layerId) => {
    if (!isUnlocked) return;
    setActiveLayer(layerId === activeLayer ? null : layerId);
    setActiveTool(null);
  };

  const handleToolClick = (e, toolId, categoryId) => {
    if (!isUnlocked) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    
    e.stopPropagation();
    setActiveTool(toolId === activeTool ? null : toolId);
    setActiveLayer(categoryId);
    
    // Initialize selected plan and user count if user has saved selections
    if (toolId !== activeTool) {
      const toolData = techStack[categoryId].tools.find(tool => tool.id === toolId);
      if (toolData && toolData.software && toolData.software !== 'None') {
        // Find the selected software to get its ID
        const selectedSoftware = toolData.availableSoftware.find(sw => sw.name === toolData.software);
        if (selectedSoftware) {
          // Find the selected plan index
          const planIndex = selectedSoftware.pricing?.findIndex(plan => 
            plan.name === toolData.selectedPlan?.name || 
            (plan.monthlyCost === toolData.selectedPlan?.monthlyCost && plan.includedUsers === toolData.selectedPlan?.includedUsers)
          );
          
          if (planIndex !== -1 && planIndex !== undefined) {
            setSelectedPlan({ 
              softwareId: selectedSoftware.id, 
              planIndex: planIndex, 
              plan: selectedSoftware.pricing[planIndex] 
            });
          }
          
          // Set user count if available
          if (toolData.userCount) {
            setUserCount(toolData.userCount);
          } else if (toolData.selectedPlan?.includedUsers) {
            setUserCount(toolData.selectedPlan.includedUsers);
          } else {
            setUserCount(1);
          }
        }
      } else {
        // Reset selections for tools with no software or "None" selected
        setSelectedPlan(null);
        setUserCount(1);
      }
    }
  };

  const handleClosePopup = () => {
    // Save the current software choice and selected pricing plan before closing
    if (currentUser && activeToolData && isUnlocked) {
      const toolIndex = techStack[activeToolData.categoryId].tools.findIndex(
        tool => tool.id === activeToolData.id
      );
      
      if (toolIndex !== -1 && (activeToolData.software || activeToolData.software === 'None')) {
        if (activeToolData.software === 'None') {
          // Handle "None" selection
          const noneSoftwareData = {
            id: 'none',
            name: 'None',
            categoryId: activeToolData.categoryId,
            toolId: activeToolData.id,
            monthlyCost: 0,
            annualCost: 0,
            selectedAt: new Date().toISOString()
          };
          
          const updatedUserTechStack = [...userTechStack.filter(item => 
            !(item.categoryId === activeToolData.categoryId && item.toolId === activeToolData.id)
          ), noneSoftwareData];
          
          console.log('Saving None selection:', noneSoftwareData);
          setUserTechStack(updatedUserTechStack);
          updateUserTechStack(currentUser.uid, updatedUserTechStack, companyId || null);
        } else if (activeToolData.software) {
          // Find the selected software from available options
          const selectedSoftware = activeToolData.availableSoftware.find(
            sw => sw.name === activeToolData.software
          );
          
          if (selectedSoftware) {
            let monthlyCost = activeToolData.monthlyCost || 0;
            let annualCost = activeToolData.annualCost || 0;
            let selectedPlanData = null;
            
            // If a pricing plan is selected, calculate costs and save plan info
            if (selectedPlan && selectedPlan.softwareId === selectedSoftware.id) {
              selectedPlanData = selectedPlan.plan;
              
              // Calculate costs based on selected plan
              if (selectedPlan.plan.tieredPricing && selectedPlan.plan.tiers && selectedPlan.plan.tiers.length > 0) {
                // For tiered pricing, use the first tier as default
                const firstTier = selectedPlan.plan.tiers[0];
                monthlyCost = parseFloat(firstTier?.baseMonthlyCost) || 0;
                annualCost = monthlyCost * 12;
              } else if (selectedPlan.plan.variablePricing) {
                // For variable pricing, use the user-entered monthly cost
                monthlyCost = parseFloat(selectedPlan.plan.monthlyCost) || 0;
                annualCost = monthlyCost * 12;
              } else {
                // For simple pricing
                if (selectedPlan.plan.usersDontMatter) {
                  monthlyCost = parseFloat(selectedPlan.plan.monthlyCost) || 0;
                  annualCost = monthlyCost * 12;
                } else {
                  // Use the first plan's cost as default
                  monthlyCost = parseFloat(selectedPlan.plan.monthlyCost) || 0;
                  annualCost = monthlyCost * 12;
                }
              }
            }
            
            const selectedSoftwareData = {
              ...selectedSoftware,
              categoryId: activeToolData.categoryId,
              toolId: activeToolData.id,
              monthlyCost,
              annualCost,
              selectedPlan: selectedPlanData,
              selectedAt: new Date().toISOString()
            };
            
            const updatedUserTechStack = [...userTechStack.filter(item => 
              !(item.categoryId === activeToolData.categoryId && item.toolId === activeToolData.id)
            ), selectedSoftwareData];
            
            setUserTechStack(updatedUserTechStack);
            updateUserTechStack(currentUser.uid, updatedUserTechStack, companyId || null);
          }
        }
      }
    }
    
    setActiveTool(null);
    setSelectedForComparison([]);
    setSelectedPlan(null);
    setUserCount(1);
    setActiveTab('select');
  };

  const handleInputChange = (e, field, toolId, categoryId) => {
    if (!isUnlocked) return;
    
    const value = e.target.value;
    
    const updatedTechStack = JSON.parse(JSON.stringify(techStack));
    const toolIndex = updatedTechStack[categoryId].tools.findIndex(tool => tool.id === toolId);
    
    if (toolIndex !== -1) {
      if (field === 'software') {
        if (value === '') {
          // Handle empty selection - clear software and costs
          updatedTechStack[categoryId].tools[toolIndex].software = '';
          updatedTechStack[categoryId].tools[toolIndex].monthlyCost = 0;
          updatedTechStack[categoryId].tools[toolIndex].annualCost = 0;
          
          // Remove from user's tech stack
          if (currentUser) {
            const updatedUserTechStack = userTechStack.filter(item => 
              !(item.categoryId === categoryId && item.toolId === toolId)
            );
            
            setUserTechStack(updatedUserTechStack);
            updateUserTechStack(currentUser.uid, updatedUserTechStack, companyId || null);
          }
        } else if (value === 'None') {
          // Handle "None" selection - set software to "None" and clear costs
          updatedTechStack[categoryId].tools[toolIndex].software = 'None';
          updatedTechStack[categoryId].tools[toolIndex].monthlyCost = 0;
          updatedTechStack[categoryId].tools[toolIndex].annualCost = 0;
          
          // Save to user's tech stack
          if (currentUser) {
            const noneSoftwareData = {
              id: 'none',
              name: 'None',
              categoryId,
              toolId,
              monthlyCost: 0,
              annualCost: 0,
              selectedAt: new Date().toISOString()
            };
            
            const updatedUserTechStack = [...userTechStack.filter(item => 
              !(item.categoryId === categoryId && item.toolId === toolId)
            ), noneSoftwareData];
            
            console.log('Saving None selection:', noneSoftwareData);
            setUserTechStack(updatedUserTechStack);
            updateUserTechStack(currentUser.uid, updatedUserTechStack, companyId || null);
          }
        } else {
          // Find the selected software from available options
          const selectedSoftware = updatedTechStack[categoryId].tools[toolIndex].availableSoftware.find(
            sw => sw.name === value
          );

          if (selectedSoftware) {
            updatedTechStack[categoryId].tools[toolIndex].software = selectedSoftware.name;
            // Description is now handled at the type level, not software level
            updatedTechStack[categoryId].tools[toolIndex].useCases = selectedSoftware.useCases || '';
            
            // Update costs if pricing is available
            if (selectedSoftware.pricing && selectedSoftware.pricing[0]) {
              let monthlyCost = 0;
              const plan = selectedSoftware.pricing[0];
              
              if (plan.tieredPricing && plan.tiers && plan.tiers.length > 0) {
                // For tiered pricing, use the base cost from the first tier
                monthlyCost = parseFloat(plan.tiers[0]?.baseMonthlyCost) || 0;
              } else if (plan.variablePricing) {
                // For variable pricing, use the user-entered monthly cost (default to 0)
                monthlyCost = parseFloat(plan.monthlyCost) || 0;
              } else {
                // For simple pricing, use the monthly cost
                monthlyCost = parseFloat(plan.monthlyCost) || 0;
              }
              
              updatedTechStack[categoryId].tools[toolIndex].monthlyCost = monthlyCost;
              updatedTechStack[categoryId].tools[toolIndex].annualCost = monthlyCost * 12;
            }

            // Save to user's tech stack
            if (currentUser) {
              const selectedSoftwareData = {
                ...selectedSoftware,
                categoryId,
                toolId,
                monthlyCost: updatedTechStack[categoryId].tools[toolIndex].monthlyCost,
                annualCost: updatedTechStack[categoryId].tools[toolIndex].annualCost,
                selectedAt: new Date().toISOString()
              };
              
              const updatedUserTechStack = [...userTechStack.filter(item => 
                !(item.categoryId === categoryId && item.toolId === toolId)
              ), selectedSoftwareData];
              
              console.log('Saving software selection:', selectedSoftwareData);
              setUserTechStack(updatedUserTechStack);
              updateUserTechStack(currentUser.uid, updatedUserTechStack, companyId || null);
            }
          }
        }
      } else {
        updatedTechStack[categoryId].tools[toolIndex][field] = value;
        
        if (field === 'monthlyCost') {
          updatedTechStack[categoryId].tools[toolIndex].annualCost = (parseFloat(value) || 0) * 12;
        } else if (field === 'annualCost') {
          updatedTechStack[categoryId].tools[toolIndex].monthlyCost = (parseFloat(value) || 0) / 12;
        }
      }
    }
    
    setTechStack(updatedTechStack);
  };

  const togglePercentages = () => {
    if (!isUnlocked) return;
    setShowPercentages(!showPercentages);
  };

  // Analyze duplicate use cases across selected software
  const analyzeDuplicateUseCases = () => {
    if (!isUnlocked) return;
    
    const useCaseMap = {};
    const duplicates = [];

    // Collect all use cases from selected software
    Object.entries(techStack).forEach(([categoryId, category]) => {
      category.tools.forEach(tool => {
        if (tool.software && tool.software !== 'None' && tool.monthlyCost > 0) {
          const selectedSoftware = tool.availableSoftware.find(sw => sw.name === tool.software);
          if (selectedSoftware && selectedSoftware.useCaseIds) {
            selectedSoftware.useCaseIds.forEach(useCaseId => {
              const useCaseIndex = parseInt(useCaseId);
              const useCase = tool.useCases[useCaseIndex];
              if (useCase) {
                if (!useCaseMap[useCase]) {
                  useCaseMap[useCase] = [];
                }
                useCaseMap[useCase].push({
                  software: tool.software,
                  category: category.title,
                  tool: tool.name,
                  monthlyCost: tool.monthlyCost
                });
              }
            });
          }
        }
      });
    });

    // Find duplicates (use cases with multiple software)
    Object.entries(useCaseMap).forEach(([useCase, softwareList]) => {
      if (new Set(softwareList.map(sw => sw.software)).size > 1) {
        duplicates.push({
          useCase,
          software: softwareList,
          totalMonthlyCost: softwareList.reduce((sum, sw) => sum + sw.monthlyCost, 0)
        });
      }
    });

    setDuplicateAnalysis(duplicates);
    setShowDuplicateAnalysis(true);
  };

  // Find the active tool's data if a popup is open
  const getActiveTool = () => {
    if (!activeTool || !activeLayer) return null;
    
    const toolData = techStack[activeLayer].tools.find(tool => tool.id === activeTool);
    return toolData ? { ...toolData, categoryId: activeLayer } : null;
  };
  
  const activeToolData = getActiveTool();

  // Order categories from top to bottom for display (Core at top)
  const orderedCategories = tierOrder;

  // Update the software selection dropdown in the popup
  const renderSoftwareSelect = (activeToolData) => {
    const availableSoftware = activeToolData.availableSoftware || [];
    
    // Sort software to show suggested ones first
    const sortedSoftware = [...availableSoftware].sort((a, b) => {
      if (a.suggested && !b.suggested) return -1;
      if (!a.suggested && b.suggested) return 1;
      return a.name.localeCompare(b.name);
    });
    
    return (
      <select 
        id="software" 
        className={`block w-full border ${darkMode ? 'border-gray-600 bg-gray-700 text-white' : 'border-gray-300 bg-white'} rounded-md shadow-sm p-2 focus:ring-red-500 focus:border-red-500`}
        value={activeToolData.software}
        onChange={(e) => handleInputChange(e, 'software', activeToolData.id, activeToolData.categoryId)}
        disabled={!isUnlocked}
      >
        <option value="">Select software</option>
        <option value="None">None</option>
        {sortedSoftware.map(software => (
          <option key={software.id} value={software.name}>
            {software.name}{software.suggested ? ' ★ (Suggested)' : ''}
          </option>
        ))}
      </select>
    );
  };

  // Show loading while checking unlock status
  if (!unlockCheckComplete) {
    return (
      <div className="min-h-screen">
        <StartSidebar />
        <div className={`w-full min-h-screen p-6 flex items-center justify-center`}>
          <div className={`text-lg ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Loading...</div>
        </div>
      </div>
    );
  }

  // If not unlocked, show locked message
  if (!isUnlocked) {
    return (
      <div className="min-h-screen">
        <StartSidebar />
        <div className={`w-full min-h-screen p-6`}>
          <div className="max-w-6xl mx-auto">
            <div className="flex justify-between items-center mb-6">
              <BackButton onClick={() => navigate('/start/technology')} />
              <h1 className={`text-3xl font-bold ${darkMode ? 'text-white' : 'text-black'} flex-1 text-center`}>Tech Stack</h1>
              <div className="w-[120px]"></div>
            </div>
            
            <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-md p-12 text-center`}>
              <Lock className={`w-16 h-16 mx-auto mb-4 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} />
              <h2 className={`text-2xl font-bold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Tech Stack Locked</h2>
              <p className={`text-lg ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                This page is currently locked. Please contact your account administrator to unlock access to the Tech Stack.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <StartSidebar />
      <div className={`w-full min-h-screen p-6`}>
        <div className="max-w-6xl mx-auto">
          <div className="flex justify-between items-center mb-6">
            <BackButton onClick={() => navigate('/start/technology')} />
            <h1 className={`text-3xl font-bold ${darkMode ? 'text-white' : 'text-black'} flex-1 text-center`}>Tech Stack</h1>
            <span 
              className={`${darkMode ? 'bg-gray-600 text-gray-400' : 'bg-gray-300 text-gray-500'} px-4 py-2 rounded-md cursor-not-allowed select-none inline-flex items-center gap-2`}
            >
              🔒 All Software
            </span>
          </div>
          
          {/* Cost Summary */}
          <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-md p-6 mb-6`}>
            {/* Toggle Button */}
            <div className="flex justify-center mb-4">
              <button 
                onClick={() => setShowDetailedCosts(!showDetailedCosts)}
                className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 transition-colors text-sm"
              >
                {showDetailedCosts ? 'Hide Details' : 'Show Details'}
              </button>
            </div>

            <div className="grid grid-cols-3 gap-6 mb-6">
              <div className={`${darkMode ? 'bg-gray-700' : 'bg-gray-50'} rounded-lg p-4 text-center`}>
                <h2 className={`text-lg font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Total Monthly Cost</h2>
                <div className="flex items-center justify-center mt-2">
                  <span className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>${costs.totalMonthly.toFixed(2)}</span>
                </div>
              </div>
              <div className={`${darkMode ? 'bg-gray-700' : 'bg-gray-50'} rounded-lg p-4 text-center`}>
                <h2 className={`text-lg font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Total Annual Cost</h2>
                <div className="flex items-center justify-center mt-2">
                  <span className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>${costs.totalAnnual.toFixed(2)}</span>
                </div>
              </div>
              <div className={`${darkMode ? 'bg-gray-700' : 'bg-gray-50'} rounded-lg p-4 text-center`}>
                <h2 className={`text-lg font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Integration</h2>
                <div className="flex items-center justify-center mt-2">
                  <span className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{costs.integrationPercentage}%</span>
                </div>
              </div>
            </div>
            
            {/* Component Cost Breakdown - Only shown when showDetailedCosts is true */}
            {showDetailedCosts && (
              <>
                <h3 className={`text-md font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-3 text-center`}>Cost Breakdown by Component</h3>
                <div className="grid grid-cols-3 gap-4">
                  {orderedCategories.map(categoryId => (
                    <div key={`cost-${categoryId}`} className={`${darkMode ? 'bg-gray-700' : 'bg-gray-50'} rounded-md p-3`}>
                      <div className="flex flex-col items-center">
                        <h4 className={`font-medium ${darkMode ? 'text-white' : 'text-gray-800'}`}>{techStack[categoryId].title}</h4>
                        <span className="text-sm font-medium text-red-400 mt-1">{costs.components[categoryId].percentOfTotal.toFixed(1)}% of total</span>
                      </div>
                      <div className="mt-2 text-center">
                        <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Monthly: <span className={`font-medium ${darkMode ? 'text-white' : 'text-gray-800'}`}>${costs.components[categoryId].monthly.toFixed(2)}</span></div>
                        <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Annual: <span className={`font-medium ${darkMode ? 'text-white' : 'text-gray-800'}`}>${costs.components[categoryId].annual.toFixed(2)}</span></div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
          
          {/* Toggle Button and Legend */}
          <div className="flex flex-col items-center mb-4 space-y-3">
            <div className="flex space-x-3">
              <button 
                onClick={togglePercentages}
                className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 transition-colors text-sm"
              >
                {showPercentages ? 'Show Apps' : 'Show Cost %'}
              </button>
              <button 
                onClick={analyzeDuplicateUseCases}
                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors text-sm"
              >
                Check Duplicates
              </button>
            </div>
            
            {/* API Status Legend */}
            <div className={`flex items-center space-x-4 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 rounded-full border-2 border-green-500 bg-gray-100"></div>
                <span>Open API Available</span>
              </div>
            </div>
          </div>
          
          {/* Message when filtered but no tools unlocked */}
          {unlockedToolIds && unlockedToolIds.length === 0 ? (
            <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-md p-8 text-center mb-6`}>
              <p className={`text-lg ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                No tech stack tools have been unlocked for your account. Please contact your administrator to unlock specific tools.
              </p>
            </div>
          ) : (
          /* Tech Stack Pyramid */
          <div className="flex flex-col items-center space-y-4 mb-12">
            {orderedCategories.map((categoryId, index) => {
              const category = techStack[categoryId];
              const delay = 0.1 + index * 0.15;
              
              return (
                <motion.div
                  key={category.id}
                  className="w-full flex justify-center"
                  initial={{ y: -100, opacity: 0 }}
                  animate={isLoaded ? { y: 0, opacity: 1 } : { y: -100, opacity: 0 }}
                  transition={{ 
                    duration: 0.5, 
                    delay: delay, 
                    type: "spring", 
                    stiffness: 100, 
                    damping: 15 
                  }}
                >
                  <motion.div 
                    className={`${category.color} rounded-lg shadow-lg p-3`}
                    style={{ width: category.width }}
                    whileHover={{ scale: 1.02 }}
                    transition={{ duration: 0.2 }}
                  >
                    <div className="flex justify-center items-center mb-3">
                      <h2 className="text-xl font-bold text-white text-center">{category.title}</h2>
                    </div>
                    
                    <div className="flex flex-wrap justify-center gap-3">
                      {category.tools.map(tool => (
                        <div key={tool.id} className="flex flex-col items-center">
                          <motion.div 
                            className={`w-10 h-10 ${darkMode ? 'bg-gray-700 border-gray-600 hover:bg-gray-600' : 'bg-gray-100 border-gray-300 hover:bg-gray-200'} rounded-full flex items-center justify-center cursor-pointer transition-colors shadow-sm border-2 overflow-hidden ${
                              tool.software && tool.availableSoftware ? 
                                (() => {
                                  const selectedSoftware = tool.availableSoftware.find(sw => sw.name === tool.software);
                                  return selectedSoftware?.open ? 'border-green-500' : 'border-transparent';
                                })() 
                                : 'border-transparent'
                            }`}
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={(e) => handleToolClick(e, tool.id, category.id)}
                          >
                            {showPercentages ? (
                              <span className={`${darkMode ? 'text-gray-300' : 'text-gray-500'} text-xs font-medium`}>
                                {tool.percentOfComponent ? Math.round(tool.percentOfComponent) + '%' : '0%'}
                              </span>
                            ) : tool.software === 'None' ? (
                              <span className={`${darkMode ? 'text-red-400' : 'text-red-600'} text-xl font-medium`}>✗</span>
                            ) : tool.software && tool.availableSoftware ? (
                              (() => {
                                const selectedSoftware = tool.availableSoftware.find(sw => sw.name === tool.software);
                                const imageKey = `${tool.id}-${tool.software}`;
                                const hasImageError = imageErrors.has(imageKey);
                                
                                if (hasImageError || !selectedSoftware?.image) {
                                  return (
                                    <span className={`${darkMode ? 'text-gray-300' : 'text-gray-500'} text-xl font-medium`}>?</span>
                                  );
                                }
                                
                                return (
                                  <img
                                    src={selectedSoftware.image}
                                    alt={tool.software}
                                    className="w-full h-full object-cover"
                                    onError={(e) => {
                                      e.target.onerror = null;
                                      e.target.src = '';
                                      setImageErrors(prev => new Set([...prev, imageKey]));
                                    }}
                                  />
                                );
                              })()
                            ) : (
                              <span className={`${darkMode ? 'text-gray-300' : 'text-gray-500'} text-xl font-medium`}>?</span>
                            )}
                          </motion.div>
                          <h3 className="font-medium text-center text-xs text-white mt-1">{tool.name}</h3>
                          {tool.software && (
                            <span className="text-xs text-red-100 mt-0.5">{tool.software}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </motion.div>
                </motion.div>
              );
            })}
          </div>
          )}
        </div>
      </div>

      {/* Tool Configuration Popup - Same as TechStack.js but with unlock check */}
      {activeTool && activeToolData && isUnlocked && (
        <motion.div 
          className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div 
            className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden`}
            initial={{ scale: 0.5, rotateY: 90 }}
            animate={{ scale: 1, rotateY: 0 }}
            transition={{ duration: 0.4, type: "spring" }}
          >
            <div className={`${techStack[activeToolData.categoryId].color} p-4`}>
              <div className="flex items-center justify-between">
                <div className="flex-1"></div>
                <h2 className="text-2xl font-bold text-white text-center flex-1">{activeToolData.name}</h2>
                <button 
                  onClick={handleClosePopup}
                  className="text-white hover:text-gray-200 flex-1 flex justify-end"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-8rem)]">
              {/* Tabs */}
              <div className={`border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'} mb-6`}>
                <nav className="-mb-px flex justify-center space-x-8">
                  <button
                    className={`border-b-2 px-1 py-4 text-sm font-medium ${
                      activeTab === 'select' 
                        ? 'border-red-500 text-red-400' 
                        : `border-transparent ${darkMode ? 'text-gray-400 hover:text-gray-300 hover:border-gray-600' : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'}`
                    }`}
                    onClick={() => setActiveTab('select')}
                  >
                    Select
                  </button>
                  <button
                    className={`border-b-2 px-1 py-4 text-sm font-medium ${
                      activeTab === 'compare' 
                        ? 'border-red-500 text-red-400' 
                        : `border-transparent ${darkMode ? 'text-gray-400 hover:text-gray-300 hover:border-gray-600' : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'}`
                    }`}
                    onClick={() => setActiveTab('compare')}
                  >
                    Compare Options
                  </button>
                  <button
                    className={`border-b-2 px-1 py-4 text-sm font-medium ${
                      activeTab === 'about' 
                        ? 'border-red-500 text-red-400' 
                        : `border-transparent ${darkMode ? 'text-gray-400 hover:text-gray-300 hover:border-gray-600' : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'}`
                    }`}
                    onClick={() => setActiveTab('about')}
                  >
                    About
                  </button>
                </nav>
              </div>

              {/* Select Tab - Simplified version, same structure as TechStack.js */}
              {activeTab === 'select' && (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 gap-4">
                    {/* None option */}
                    <div 
                      className={`border rounded-lg p-4 ${
                        activeToolData.software === 'None' 
                          ? (darkMode ? 'border-red-500 bg-red-900/20' : 'border-red-500 bg-red-50') 
                          : (darkMode ? 'border-gray-600' : 'border-gray-200')
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          <div className="flex-shrink-0 w-12 h-12">
                            <div className={`w-full h-full ${darkMode ? 'bg-gray-700' : 'bg-gray-100'} rounded-lg flex items-center justify-center`}>
                              <span className={`${darkMode ? 'text-red-400' : 'text-red-600'} text-xl`}>✗</span>
                            </div>
                          </div>
                          <div>
                            <h3 className={`text-lg font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                              None
                            </h3>
                          </div>
                        </div>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => {
                              const updatedTechStack = JSON.parse(JSON.stringify(techStack));
                              const toolIndex = updatedTechStack[activeToolData.categoryId].tools.findIndex(
                                tool => tool.id === activeToolData.id
                              );
                              if (toolIndex !== -1) {
                                updatedTechStack[activeToolData.categoryId].tools[toolIndex].software = 'None';
                                updatedTechStack[activeToolData.categoryId].tools[toolIndex].monthlyCost = 0;
                                updatedTechStack[activeToolData.categoryId].tools[toolIndex].annualCost = 0;
                                setTechStack(updatedTechStack);
                                
                                // Save to user's tech stack
                                if (currentUser) {
                                  const noneSoftwareData = {
                                    id: 'none',
                                    name: 'None',
                                    categoryId: activeToolData.categoryId,
                                    toolId: activeToolData.id,
                                    monthlyCost: 0,
                                    annualCost: 0,
                                    selectedAt: new Date().toISOString()
                                  };
                                  
                                  const updatedUserTechStack = [...userTechStack.filter(item => 
                                    !(item.categoryId === activeToolData.categoryId && item.toolId === activeToolData.id)
                                  ), noneSoftwareData];
                                  
                                  console.log('Saving None selection:', noneSoftwareData);
                                  setUserTechStack(updatedUserTechStack);
                                  updateUserTechStack(currentUser.uid, updatedUserTechStack, companyId || null);
                                }
                              }
                            }}
                            className={`px-3 py-1 rounded-md text-sm font-medium ${
                              activeToolData.software === 'None'
                                ? (darkMode ? 'bg-red-900 text-red-200' : 'bg-red-100 text-red-700')
                                : 'bg-red-600 text-white hover:bg-red-700'
                            }`}
                          >
                            {activeToolData.software === 'None' ? 'Selected' : 'Select'}
                          </button>
                        </div>
                      </div>
                    </div>
                    
                    {/* Software options - same as TechStack.js */}
                    {activeToolData.availableSoftware?.map((software) => (
                      <div 
                        key={software.id}
                        className={`border rounded-lg p-4 ${
                          software.name === activeToolData.software 
                            ? (darkMode ? 'border-red-500 bg-red-900/20' : 'border-red-500 bg-red-50') 
                            : (darkMode ? 'border-gray-600' : 'border-gray-200')
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-4">
                            <div className="flex-shrink-0 w-12 h-12">
                              {software.image ? (
                                (() => {
                                  const imageKey = `popup-${software.id}`;
                                  const hasImageError = imageErrors.has(imageKey);
                                  
                                  if (hasImageError) {
                                    return (
                                      <div className={`w-full h-full ${darkMode ? 'bg-gray-700' : 'bg-gray-100'} rounded-lg flex items-center justify-center`}>
                                        <span className={`${darkMode ? 'text-gray-400' : 'text-gray-400'} text-xl`}>?</span>
                                      </div>
                                    );
                                  }
                                  
                                  return (
                                    <img 
                                      src={software.image} 
                                      alt={software.name}
                                      className="w-full h-full object-cover rounded-lg"
                                      onError={(e) => {
                                        e.target.onerror = null;
                                        e.target.src = '';
                                        setImageErrors(prev => new Set([...prev, imageKey]));
                                      }}
                                    />
                                  );
                                })()
                              ) : (
                                <div className={`w-full h-full ${darkMode ? 'bg-gray-700' : 'bg-gray-100'} rounded-lg flex items-center justify-center`}>
                                  <span className={`${darkMode ? 'text-gray-400' : 'text-gray-400'} text-xl`}>?</span>
                                </div>
                              )}
                            </div>
                            <div className="flex items-center space-x-2">
                              <h3 className={`text-lg font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                                {software.name}
                                {software.suggested && (
                                  <span className="ml-2 text-red-400">★ Suggested</span>
                                )}
                              </h3>
                              {software.open && (
                                <span className="bg-green-100 text-green-800 text-xs font-medium px-2 py-1 rounded-full">
                                  Open API
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex space-x-2">
                            <button
                              onClick={() => {
                                const updatedTechStack = JSON.parse(JSON.stringify(techStack));
                                const toolIndex = updatedTechStack[activeToolData.categoryId].tools.findIndex(
                                  tool => tool.id === activeToolData.id
                                );
                                if (toolIndex !== -1) {
                                  updatedTechStack[activeToolData.categoryId].tools[toolIndex].software = software.name;
                                  if (software.pricing && software.pricing[0]) {
                                    let monthlyCost = 0;
                                    const plan = software.pricing[0];
                                    
                                    if (plan.tieredPricing && plan.tiers && plan.tiers.length > 0) {
                                      monthlyCost = parseFloat(plan.tiers[0]?.baseMonthlyCost) || 0;
                                    } else if (plan.variablePricing) {
                                      monthlyCost = parseFloat(plan.monthlyCost) || 0;
                                    } else {
                                      monthlyCost = parseFloat(plan.monthlyCost) || 0;
                                    }
                                    
                                    updatedTechStack[activeToolData.categoryId].tools[toolIndex].monthlyCost = monthlyCost;
                                    updatedTechStack[activeToolData.categoryId].tools[toolIndex].annualCost = monthlyCost * 12;
                                  }
                                  setTechStack(updatedTechStack);
                                  
                                  // Save to user's tech stack
                                  if (currentUser) {
                                    const selectedSoftwareData = {
                                      ...software,
                                      categoryId: activeToolData.categoryId,
                                      toolId: activeToolData.id,
                                      monthlyCost: updatedTechStack[activeToolData.categoryId].tools[toolIndex].monthlyCost,
                                      annualCost: updatedTechStack[activeToolData.categoryId].tools[toolIndex].annualCost,
                                      selectedAt: new Date().toISOString()
                                    };
                                    
                                    const updatedUserTechStack = [...userTechStack.filter(item => 
                                      !(item.categoryId === activeToolData.categoryId && item.toolId === activeToolData.id)
                                    ), selectedSoftwareData];
                                    
                                    console.log('Saving software selection:', selectedSoftwareData);
                                    setUserTechStack(updatedUserTechStack);
                                    updateUserTechStack(currentUser.uid, updatedUserTechStack, companyId || null);
                                  }
                                }
                              }}
                              className={`px-3 py-1 rounded-md text-sm font-medium ${
                                software.name === activeToolData.software
                                  ? (darkMode ? 'bg-red-900 text-red-200' : 'bg-red-100 text-red-700')
                                  : 'bg-red-600 text-white hover:bg-red-700'
                              }`}
                            >
                              {software.name === activeToolData.software ? 'Selected' : 'Select'}
                            </button>
                            {software.signupUrl && (
                              <a
                                href={software.signupUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="px-3 py-1 rounded-md text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 transition-colors"
                              >
                                Sign Up
                              </a>
                            )}
                          </div>
                        </div>
                        
                        {/* Pricing Plans - Show when selected - Same structure as TechStack.js */}
                        {software.name === activeToolData.software && software.pricing && software.pricing.length > 0 && (
                          <div className="mt-4 pt-4 border-t border-gray-200">
                            <h4 className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-3`}>Available Plans</h4>

                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                              {software.pricing.map((plan, index) => {
                                const isSelected = selectedPlan && selectedPlan.softwareId === software.id && selectedPlan.planIndex === index;
                                
                                // Calculate cost based on pricing type
                                let totalMonthlyCost;
                                if (plan.tieredPricing) {
                                  totalMonthlyCost = calculateTieredPricingCost(plan, userCount);
                                } else {
                                  const includedUsers = Number(plan.includedUsers) || 0;
                                  const additionalUsers = Math.max(0, userCount - includedUsers);
                                  const additionalCost = additionalUsers * (Number(plan.costPerAdditionalUser) || 0);
                                  totalMonthlyCost = Number(plan.monthlyCost) + additionalCost;
                                }
                                
                                return (
                                  <div 
                                    key={index}
                                    className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                                      isSelected
                                        ? (darkMode ? 'border-red-500 bg-red-900/20' : 'border-red-500 bg-red-50')
                                        : (darkMode ? 'bg-gray-700 border-gray-600 hover:border-gray-500' : 'bg-gray-50 border-gray-200 hover:border-gray-300')
                                    }`}
                                    onClick={() => {
                                      setSelectedPlan({ softwareId: software.id, planIndex: index, plan });
                                      setUserCount(Number(plan.includedUsers) || 1);
                                    }}
                                  >
                                    <div className="flex items-center justify-between mb-2">
                                      <h5 className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                                        {plan.name || `Plan ${index + 1}`}
                                      </h5>
                                      {plan.usersDontMatter ? (
                                        <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                          Flat rate
                                        </span>
                                      ) : plan.includedUsers && (
                                        <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                          {plan.includedUsers} users
                                        </span>
                                      )}
                                    </div>
                                    <div className="space-y-1">
                                      {plan.tieredPricing && plan.tiers && plan.tiers.length > 0 ? (
                                        <>
                                          <div className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                                            ${totalMonthlyCost}/month
                                          </div>
                                          <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                            ${(totalMonthlyCost * 12).toFixed(2)}/year
                                          </div>
                                        </>
                                      ) : plan.variablePricing ? (
                                        <>
                                          <div className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                                            ${plan.monthlyCost || 'Enter cost'}/month
                                          </div>
                                          <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                            Variable pricing
                                          </div>
                                        </>
                                      ) : (
                                        <>
                                          <div className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                                            ${totalMonthlyCost}/month
                                          </div>
                                          <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                            ${(totalMonthlyCost * 12).toFixed(2)}/year
                                          </div>
                                        </>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                            
                            {/* Variable Pricing Input */}
                            {selectedPlan && selectedPlan.plan && selectedPlan.plan.variablePricing && (
                              <div className="mt-4 pt-4 border-t border-gray-200">
                                <h4 className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-3`}>Enter Your Monthly Cost</h4>
                                <div className="max-w-xs">
                                  <input
                                    type="text"
                                    value={selectedPlan.plan.monthlyCost || ''}
                                    onChange={(e) => {
                                      const updatedPlan = { ...selectedPlan.plan, monthlyCost: e.target.value };
                                      setSelectedPlan({ ...selectedPlan, plan: updatedPlan });
                                      
                                      // Update the tech stack with the new cost
                                      const updatedTechStack = JSON.parse(JSON.stringify(techStack));
                                      const toolIndex = updatedTechStack[activeToolData.categoryId].tools.findIndex(
                                        tool => tool.id === activeToolData.id
                                      );
                                      if (toolIndex !== -1) {
                                        const monthlyCost = parseFloat(e.target.value) || 0;
                                        updatedTechStack[activeToolData.categoryId].tools[toolIndex].monthlyCost = monthlyCost;
                                        updatedTechStack[activeToolData.categoryId].tools[toolIndex].annualCost = monthlyCost * 12;
                                        setTechStack(updatedTechStack);
                                        
                                        // Save to user's tech stack
                                        if (currentUser) {
                                          const selectedSoftware = activeToolData.availableSoftware.find(sw => sw.name === activeToolData.software);
                                          if (selectedSoftware) {
                                            const selectedSoftwareData = {
                                              ...selectedSoftware,
                                              categoryId: activeToolData.categoryId,
                                              toolId: activeToolData.id,
                                              monthlyCost: monthlyCost,
                                              annualCost: monthlyCost * 12,
                                              selectedAt: new Date().toISOString()
                                            };
                                            
                                            const updatedUserTechStack = [...userTechStack.filter(item => 
                                              !(item.categoryId === activeToolData.categoryId && item.toolId === activeToolData.id)
                                            ), selectedSoftwareData];
                                            
                                            setUserTechStack(updatedUserTechStack);
                                            updateUserTechStack(currentUser.uid, updatedUserTechStack, companyId || null);
                                          }
                                        }
                                      }
                                    }}
                                    placeholder="e.g., $150"
                                    className={`block w-full rounded-md border ${darkMode ? 'border-gray-600 bg-gray-700 text-white' : 'border-gray-300'} shadow-sm p-2 focus:ring-red-500 focus:border-red-500`}
                                  />
                                </div>
                                <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'} mt-1`}>
                                  Enter the amount you pay monthly for this software
                                </p>
                              </div>
                            )}

                            {/* User Count Input for User-Based Pricing */}
                            {selectedPlan && selectedPlan.plan && (!selectedPlan.plan.usersDontMatter || selectedPlan.plan.tieredPricing) && (
                              <div className="mt-4 pt-4 border-t border-gray-200">
                                <h4 className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-3`}>Number of Users</h4>
                                <div className="max-w-xs">
                                  <input
                                    type="number"
                                    min="1"
                                    value={userCount}
                                    onChange={(e) => {
                                      const newUserCount = parseInt(e.target.value) || 1;
                                      setUserCount(newUserCount);
                                      
                                      // Calculate costs based on user count and pricing type
                                      const plan = selectedPlan.plan;
                                      let totalMonthlyCost;
                                      
                                      if (plan.tieredPricing) {
                                        totalMonthlyCost = calculateTieredPricingCost(plan, newUserCount);
                                      } else {
                                        const includedUsers = Number(plan.includedUsers) || 0;
                                        const additionalUsers = Math.max(0, newUserCount - includedUsers);
                                        const additionalCost = additionalUsers * (Number(plan.costPerAdditionalUser) || 0);
                                        totalMonthlyCost = Number(plan.monthlyCost) + additionalCost;
                                      }
                                      
                                      // Update the tech stack with the new cost
                                      const updatedTechStack = JSON.parse(JSON.stringify(techStack));
                                      const toolIndex = updatedTechStack[activeToolData.categoryId].tools.findIndex(
                                        tool => tool.id === activeToolData.id
                                      );
                                      if (toolIndex !== -1) {
                                        updatedTechStack[activeToolData.categoryId].tools[toolIndex].monthlyCost = totalMonthlyCost;
                                        updatedTechStack[activeToolData.categoryId].tools[toolIndex].annualCost = totalMonthlyCost * 12;
                                        setTechStack(updatedTechStack);
                                        
                                        // Save to user's tech stack
                                        if (currentUser) {
                                          const selectedSoftware = activeToolData.availableSoftware.find(sw => sw.name === activeToolData.software);
                                          if (selectedSoftware) {
                                            const selectedSoftwareData = {
                                              ...selectedSoftware,
                                              categoryId: activeToolData.categoryId,
                                              toolId: activeToolData.id,
                                              monthlyCost: totalMonthlyCost,
                                              annualCost: totalMonthlyCost * 12,
                                              userCount: newUserCount,
                                              selectedAt: new Date().toISOString()
                                            };
                                            
                                            const updatedUserTechStack = [...userTechStack.filter(item => 
                                              !(item.categoryId === activeToolData.categoryId && item.toolId === activeToolData.id)
                                            ), selectedSoftwareData];
                                            
                                            setUserTechStack(updatedUserTechStack);
                                            updateUserTechStack(currentUser.uid, updatedUserTechStack, companyId || null);
                                          }
                                        }
                                      }
                                    }}
                                    placeholder="e.g., 5"
                                    className={`block w-full rounded-md border ${darkMode ? 'border-gray-600 bg-gray-700 text-white' : 'border-gray-300'} shadow-sm p-2 focus:ring-red-500 focus:border-red-500`}
                                  />
                                </div>
                                <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'} mt-1`}>
                                  {selectedPlan.plan.tieredPricing ? 
                                    'Cost varies by user count and tier' : 
                                    `${selectedPlan.plan.includedUsers ? `${selectedPlan.plan.includedUsers} users included, ` : ''}${selectedPlan.plan.costPerAdditionalUser || '0'}/additional user`
                                  }
                                </p>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Compare Options Tab - Simplified version */}
              {activeTab === 'compare' && (
                <div className="space-y-6">
                  <div className="mt-4">
                    <h3 className={`text-lg font-medium ${darkMode ? 'text-white' : 'text-gray-900'} mb-4`}>All Available Options</h3>
                    <div className={`overflow-x-auto ${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg border ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className={`${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                          <tr>
                            <th className={`px-6 py-3 text-left text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider`}>
                              Software
                            </th>
                            <th className={`px-6 py-3 text-left text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider`}>
                              Monthly Cost
                            </th>
                            <th className={`px-6 py-3 text-left text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider`}>
                              Open API
                            </th>
                            <th className={`px-6 py-3 text-left text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider`}>
                              Suggested
                            </th>
                          </tr>
                        </thead>
                        <tbody className={`${darkMode ? 'bg-gray-800' : 'bg-white'} divide-y ${darkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
                          {activeToolData.availableSoftware?.map((software) => (
                            <tr 
                              key={software.id}
                              className={`cursor-pointer transition-colors ${
                                selectedForComparison.includes(software.id)
                                  ? (darkMode ? 'bg-green-900/20' : 'bg-green-50')
                                  : (darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50')
                              }`}
                              onClick={() => {
                                setSelectedForComparison(prev => {
                                  if (prev.includes(software.id)) {
                                    return prev.filter(id => id !== software.id);
                                  } else {
                                    return [...prev, software.id];
                                  }
                                });
                              }}
                            >
                              <td className={`px-6 py-4 whitespace-nowrap ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                                <div className="flex items-center space-x-3">
                                  <div className="flex-shrink-0 w-8 h-8">
                                    {software.image ? (
                                      (() => {
                                        const imageKey = `compare-${software.id}`;
                                        const hasImageError = imageErrors.has(imageKey);
                                        
                                        if (hasImageError) {
                                          return (
                                            <div className={`w-full h-full ${darkMode ? 'bg-gray-700' : 'bg-gray-100'} rounded flex items-center justify-center`}>
                                              <span className={`${darkMode ? 'text-gray-400' : 'text-gray-400'} text-sm`}>?</span>
                                            </div>
                                          );
                                        }
                                        
                                        return (
                                          <img 
                                            src={software.image} 
                                            alt={software.name}
                                            className="w-full h-full object-cover rounded"
                                            onError={(e) => {
                                              e.target.onerror = null;
                                              e.target.src = '';
                                              setImageErrors(prev => new Set([...prev, imageKey]));
                                            }}
                                          />
                                        );
                                      })()
                                    ) : (
                                      <div className={`w-full h-full ${darkMode ? 'bg-gray-700' : 'bg-gray-100'} rounded flex items-center justify-center`}>
                                        <span className={`${darkMode ? 'text-gray-400' : 'text-gray-400'} text-sm`}>?</span>
                                      </div>
                                    )}
                                  </div>
                                  <div>
                                    <div className="text-sm font-medium">
                                      {software.name}
                                      {selectedForComparison.includes(software.id) && (
                                        <span className="ml-2 text-green-600">✓</span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </td>
                              <td className={`px-6 py-4 whitespace-nowrap text-sm ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                                ${software.pricing?.[0]?.monthlyCost || 'N/A'}
                              </td>
                              <td className={`px-6 py-4 whitespace-nowrap text-sm ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                                {software.open ? (
                                  <span className="text-green-600 font-medium">✓ Yes</span>
                                ) : (
                                  <span className="text-red-600 font-medium">✗ No</span>
                                )}
                              </td>
                              <td className={`px-6 py-4 whitespace-nowrap text-sm ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                                {software.suggested ? (
                                  <span className="text-red-600 font-medium">★ Yes</span>
                                ) : (
                                  <span className="text-gray-500">-</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {/* About Tab */}
              {activeTab === 'about' && (
                <div className="space-y-6">
                  <div className={`${darkMode ? 'bg-gray-700' : 'bg-gray-50'} rounded-lg p-6`}>
                    <h3 className={`text-lg font-medium ${darkMode ? 'text-white' : 'text-gray-900'} mb-4`}>About {activeToolData.name}</h3>
                    <div className="prose prose-sm max-w-none">
                      <p className={`${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                        {activeToolData.description || 'No description available for this type.'}
                      </p>
                    </div>
                  </div>

                  <div className={`${darkMode ? 'bg-gray-700' : 'bg-gray-50'} rounded-lg p-6`}>
                    <h3 className={`text-lg font-medium ${darkMode ? 'text-white' : 'text-gray-900'} mb-4`}>Use Cases</h3>
                    <div className="prose prose-sm max-w-none">
                      {activeToolData.useCases && activeToolData.useCases.length > 0 ? (
                        <ul className={`list-disc pl-5 space-y-2 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                          {activeToolData.useCases.map((useCase, index) => (
                            <li key={index}>{useCase}</li>
                          ))}
                        </ul>
                      ) : (
                        <p className={`${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                          No use cases available for this type.
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            <div className={`px-6 py-4 ${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'} border-t`}>
              <div className="flex justify-end">
                <button 
                  onClick={handleClosePopup}
                  className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 transition-colors"
                >
                  Save & Close
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* Duplicate Analysis Modal - Same as TechStack.js */}
      {showDuplicateAnalysis && (
        <motion.div 
          className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div 
            className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden`}
            initial={{ scale: 0.5, rotateY: 90 }}
            animate={{ scale: 1, rotateY: 0 }}
            transition={{ duration: 0.4, type: "spring" }}
          >
            <div className="bg-blue-600 p-4">
              <div className="flex items-center justify-between">
                <div className="flex-1"></div>
                <h2 className="text-2xl font-bold text-white text-center flex-1">Duplicate Use Case Analysis</h2>
                <button 
                  onClick={() => setShowDuplicateAnalysis(false)}
                  className="text-white hover:text-gray-200 flex-1 flex justify-end"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-8rem)]">
              {duplicateAnalysis.length > 0 ? (
                <div className="space-y-6">
                  <div className={`text-center p-4 ${darkMode ? 'bg-gray-700' : 'bg-blue-50'} rounded-lg`}>
                    <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-blue-800'}`}>
                      Found {duplicateAnalysis.length} use case(s) with multiple software solutions. Consider removing redundant software to reduce costs.
                    </p>
                  </div>
                  
                  {duplicateAnalysis.map((duplicate, index) => (
                    <div key={index} className={`${darkMode ? 'bg-gray-700' : 'bg-gray-50'} rounded-lg p-4`}>
                      <h3 className={`text-lg font-medium ${darkMode ? 'text-white' : 'text-gray-900'} mb-3`}>
                        {duplicate.useCase}
                      </h3>
                      <div className="space-y-3">
                        {duplicate.software.map((sw, swIndex) => (
                          <div key={swIndex} className={`${darkMode ? 'bg-gray-600' : 'bg-white'} rounded-md p-3 border ${darkMode ? 'border-gray-500' : 'border-gray-200'}`}>
                            <div className="flex justify-between items-center">
                              <div>
                                <h4 className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{sw.software}</h4>
                                <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                  {sw.category} • {sw.tool}
                                </p>
                              </div>
                              <div className="text-right">
                                <p className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>${sw.monthlyCost}/month</p>
                                <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>${(sw.monthlyCost * 12).toFixed(2)}/year</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className={`mt-3 p-3 ${darkMode ? 'bg-red-900/20' : 'bg-red-50'} rounded-md`}>
                        <p className={`text-sm font-medium ${darkMode ? 'text-red-300' : 'text-red-800'}`}>
                          Total monthly cost for this use case: ${duplicate.totalMonthlyCost.toFixed(2)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className={`text-center p-8 ${darkMode ? 'bg-gray-700' : 'bg-green-50'} rounded-lg`}>
                  <h3 className={`text-lg font-medium ${darkMode ? 'text-white' : 'text-green-800'} mb-2`}>
                    No Duplicates Found! 🎉
                  </h3>
                  <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-green-700'}`}>
                    Your tech stack is well-optimized with no overlapping use cases detected.
                  </p>
                </div>
              )}
            </div>
            
            <div className={`px-6 py-4 ${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'} border-t`}>
              <div className="flex justify-end">
                <button 
                  onClick={() => setShowDuplicateAnalysis(false)}
                  className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
};

export default StartTechStack;

